import {getStartOfDay} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import {parseISO, isSameDay, getISOWeek} from 'date-fns';
import groupBy from 'lodash/groupBy';
import createCachedSelector from 're-reselect';
import {getParkingQuotaProfile} from '../../data/profiles/selectors';
import {getStartOfTodayISOString} from '../../data/settings/selectors';
import {RootState} from '../../rootReducer';
import {parkingReservationsAdapter} from './adapters';
import {findParkingReservationByDate} from './utils';

export const {
  selectById: getParkingReservationsById,
  selectIds: getParkingReservationsIds,
  selectEntities: getParkingReservationsEntities,
  selectAll: getAllParkingReservations,
  selectTotal: getTotalParkingReservations,
} = parkingReservationsAdapter.getSelectors((state: RootState) => state.data.parkingReservation.parkingReservations);

export const getIsLoadingParkingReservations = (state: RootState) =>
  state.data.parkingReservation.isLoadingParkingReservations;

export const getIsLoadingParkingCount = (state: RootState) => state.data.parkingReservation.isLoadingParkingCount;

export const getIsReservingParkingSpot = (state: RootState) => state.data.parkingReservation.isReservingParkingSpot;

export const getIsRemovingParkingSpot = (state: RootState) => state.data.parkingReservation.isRemovingParkingSpot;

export const getParkingReservationByDate = createCachedSelector(
  getAllParkingReservations,
  (_state: RootState, date: string) => date,
  findParkingReservationByDate,
)((_state, date) => date || '');

export const getParkingReservationForToday = createSelector(
  getAllParkingReservations,
  getStartOfTodayISOString,
  (reservations, todayISOString) =>
    reservations.find((dateParkingReservation) =>
      isSameDay(getStartOfDay(dateParkingReservation.date), parseISO(todayISOString)),
    ),
);

export const getParkingWeekReservationsPerDate = createCachedSelector(
  getAllParkingReservations,
  getParkingQuotaProfile,
  (_state: RootState, date: Date | undefined) => date,
  (parkingItems, parkingQuotaProfile, date) => {
    const weekNumber = getISOWeek(date!);
    const parkingsPerWeek = groupBy(parkingItems, (parking) => getISOWeek(getStartOfDay(parking.date)));
    return {
      bookingCount: weekNumber in parkingsPerWeek ? parkingsPerWeek[weekNumber].length : 0,
      maxBookings: parkingQuotaProfile.daysPerWeek,
    };
  },
)((_state, date) => date?.toISOString() ?? '');

export const getParkingReservationsGroupedByDate = createSelector(
  getAllParkingReservations,
  (allParkingReservations) => {
    return groupBy(allParkingReservations, 'date');
  },
);
